// Step-by-step "how to fill out an estimate request" guide for AMs
// (George 2026-07-01: reps were submitting question-only requests with no
// lines — walk them through the whole flow). Self-contained: renders the
// 📖 How-To Guide button + its modal. Content mirrors
// docs/REQUEST-FILL-GUIDE.md — update both together.
import React, { useState } from 'react';
import { styles } from './UI';

const ORANGE = '#E36F2C';

function Step({ n, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
      <div style={{
        flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
        background: '#1F2937', color: '#fff', fontSize: 13, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{n}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1F2937', marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  );
}

const li = { marginBottom: 3 };
const ul = { margin: '4px 0 0 0', paddingLeft: 18 };

export default function RequestGuide() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button style={{
          ...styles.btnSecondary, whiteSpace: 'nowrap',
          border: `2px solid ${ORANGE}`, color: ORANGE, background: '#FFF7ED',
          fontWeight: 700, boxShadow: '0 1px 3px rgba(227,111,44,0.3)',
        }}
        title="Step-by-step: how to fill out an estimate request"
        onClick={() => setOpen(true)}>
        📖 How-To Guide
      </button>
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 8, width: 760, maxWidth: '95vw',
            maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1F2937' }}>
                  How to Fill Out an Estimate Request
                </div>
                <div style={{ fontSize: 11.5, color: '#6B7280' }}>
                  The request is what leadership prices from — the more complete it is, the faster and more accurate your quote.
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{
                border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280',
              }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px', overflowY: 'auto' }}>

              <Step n={1} title="Before you start — have a Project Tracker opportunity">
                Every request must link to an opportunity (that's where the estimate, quote, and customer
                files land). If the deal isn't in your Project Tracker yet, add it there first.
              </Step>

              <Step n={2} title="Create the request and fill the header">
                Click <strong>+ New Request</strong>, then give it a name (job / site / outage name). Set the three
                required header fields:
                <ul style={ul}>
                  <li style={li}><strong>Customer</strong> — pick from the list, or type the name if they're not in CRM yet.</li>
                  <li style={li}><strong>Opportunity</strong> — link your Project Tracker entry. Submit is blocked without it.
                    An entry marked <strong>· unassigned</strong> is one leadership created when they priced the deal
                    without a request; picking it claims it into your tracker with the existing estimate still attached,
                    so you just fill in the questionnaire.</li>
                  <li style={li}><strong>Quote Needed By</strong> — when the customer needs pricing in hand.</li>
                </ul>
              </Step>

              <Step n={3} title="Select scope flags — everything the customer needs">
                Click every service in scope (Labor, Safety Techs, Safety Professional, Rescue, Portable Air,
                HP/LP Fixed Air, Gas Detection, Radios, CSVM, Tents, Site Utilities, Vehicles, MUSTER – Time
                &amp; Attendance). The flags control which questionnaire sections appear — a missed flag means
                missed questions and a hole in the estimate.
              </Step>

              <Step n={4} title="Answer the questionnaire">
                Work through every active question. <strong>PROJECT INFO Q1–Q6</strong> (project name,
                customer/location, start date, finish date, salesperson, main shift hours) are required before
                submit. If the customer has an <strong>MSA or set pricing</strong> (Q7), attach it on the Files tab —
                it drives the MSA Rates tab. Click <strong>💾 Save Answers</strong> as you go; answers don't save themselves.
              </Step>

              <Step n={5} title="Labor tab — one line per craft and shift">
                This is what leadership prices the hours from. For each position:
                <ul style={ul}>
                  <li style={li}><strong>Craft</strong> — search the crafts list; put the exact MSA / Set Pricing wording in the second box so the quote matches the contract.</li>
                  <li style={li}><strong>PAY vs BILL</strong> — Pay is what SSS pays the employee; Bill is what the customer is charged. Enter the rates you know (MSA / Set Pricing especially); leave unknowns blank — leadership sets final pricing.</li>
                  <li style={li}><strong>Phases (required)</strong> — every line needs at least one phase with dates and day/night headcount. No phases = no hours = nothing to price. Submit is blocked until every line has one.</li>
                  <li style={li}><strong>Rate Only</strong> — check it when the customer just wants a rate on the quote for a position we're not staffing (no phases needed).</li>
                  <li style={li}><strong>Per diem travel days</strong> — defaults to 2 (day before mob-in + day after mob-out); adjust per line if needed.</li>
                  <li style={li}>If a confirmed MSA sheet exists, use <strong>＄ From MSA…</strong> to add pre-priced lines.</li>
                </ul>
              </Step>

              <Step n={6} title="Equipment tab — search the catalog first">
                Use the orange <strong>🔍 catalog search</strong> to add equipment — it carries the CAT-CLASS that
                matches the line to Rental Man stock downstream. <strong>+ Add Non-Inventory Equipment</strong> is only
                for items we don't stock. Per line: qty, <strong>Start/End dates</strong> (Days auto-fills), and a
                target usage rate (MSA pricing if applicable, your best number otherwise, blank if unknown).
                <strong> Dup</strong> clones a line for ramp up/down; <strong>⤓ all</strong> copies one line's dates to every line.
              </Step>

              <Step n={7} title="Consumables, Services and Charges tab">
                Add consumables, services, one-time charges, mobilization, and air sales — pick the right
                <strong> Category</strong> per line. Enter the cost; the default markup % fills the bill rate
                automatically (adjust per line if the deal calls for it).
              </Step>

              <Step n={8} title="MSA Rates tab — confirm the extracted sheet">
                If you attached an MSA, the AI-extracted rate sheet shows here. Review the lines against the
                contract and <strong>Confirm</strong> it — that unlocks the ＄ From MSA pickers on Labor and Equipment.
              </Step>

              <Step n={9} title="Files tab — attach the key documents">
                Attach everything the customer sent: <strong>bid package, MSA / pricing agreement, entry or work
                schedules, site rules, safety specs, T&Cs</strong>. Files follow every revision of the request and
                surface on the linked opportunity for leadership.
                <ul style={ul}>
                  <li style={li}><strong>Keep uploading as documents arrive</strong> — before submit, while estimating
                    is in progress, and after the estimate is built. Don't sit on a schedule or a revised bid
                    package because the request is already in.</li>
                  <li style={li}>Once the request is submitted, <strong>every new upload automatically emails the
                    estimating chain</strong> (your supervisor, the estimating team, and you) with the document
                    attached and the request name in the subject — you never need to tell anyone to go look.</li>
                </ul>
              </Step>

              <Step n={10} title="Submit">
                Submit runs the checks: customer set, opportunity linked, Q1–Q6 answered,
                <strong> at least one labor / equipment / consumable line</strong> (questions alone can't be
                estimated), and every non-rate-only labor line phased. Your supervisor and the estimating team
                get an email with a summary of the request.
              </Step>

              <Step n={11} title="Revisions — when something changes after submit">
                Two situations, two paths:
                <ul style={ul}>
                  <li style={li}><strong>Leadership returned it</strong> — an amber banner shows what's missing.
                    The request is back in Draft: fix the noted items, then <strong>Submit</strong> again.
                    The banner clears on resubmit.</li>
                  <li style={li}><strong>The customer changed scope</strong> (dates moved, headcount changed, new
                    service added) — open the request and click <strong>📝 New Revision</strong>. It clones the whole
                    request into an editable -1 / -2 copy; the old revision is frozen as the audit trail and your
                    files carry over automatically. Make the changes, then Submit — leadership sees a change-order
                    flag on the estimate so nothing slips through verbally.</li>
                  <li style={li}><strong>Never start a duplicate request</strong> for the same job — revisions keep
                    the whole history in one chain.</li>
                </ul>
              </Step>

              <div style={{
                background: '#FEF3C7', border: '1px solid #FBBF24', borderRadius: 6,
                padding: '10px 14px', fontSize: 12.5, color: '#92400E', marginTop: 4,
              }}>
                <strong>Bottom line:</strong> a request with no labor or equipment lines is just a questionnaire —
                there's nothing to estimate. Enter every position and piece of equipment the job needs, with the
                best rates you have, and leave what you don't know blank rather than guessing.
              </div>
            </div>
            <div style={{ padding: '10px 20px', borderTop: '1px solid #E5E7EB', textAlign: 'right' }}>
              <button style={{ ...styles.btn, background: ORANGE }} onClick={() => setOpen(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
